// src/hooks/useCredentials.jsx — SOUND-709
//
// Was: fetch every credential, no params, no pagination, and — because the API
// defaulted to `midId: null` — silently excluding the 27 portal credentials the
// page most needed to show.
//
// Now the SOUND-571 list contract, the same shape /mids and /tickets use:
// scope -> filter -> search -> sort -> page, with counts aggregated server-side
// over the same filter as the page.
import { useState, useEffect, useCallback, useMemo } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";

const EMPTY_SUMMARY = { typeCounts: {}, noSecretStorage: 0 };

export function useCredentials({
  page = 1,
  limit = 25,
  q = "",
  provider = "",
  credentialType = "",
  sortBy = "createdAt",
  sortDir = "desc",
  companyId: scopedCompanyId = null,
} = {}) {
  const [credentials, setCredentials] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [pagination, setPagination] = useState({ page, limit, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featureGated, setFeatureGated] = useState(false);

  const { user } = useAuth();
  const companyId = user?.companyId;

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("limit", String(limit));
    p.set("sortBy", sortBy);
    p.set("sortDir", sortDir);
    if (q) p.set("q", q);
    if (provider) p.set("provider", provider);
    if (credentialType) p.set("credentialType", credentialType);
    // The company-scoped tab passes a companyId. The server still applies
    // credentialScope() on top, so this NARROWS the scope and can never widen
    // it — an internal user viewing one client sees that client, and a client
    // passing someone else's id still gets their own filter applied.
    if (scopedCompanyId) p.set("companyId", scopedCompanyId);
    return p.toString();
  }, [page, limit, q, provider, credentialType, sortBy, sortDir, scopedCompanyId]);

  const fetchCredentials = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest(
        "get",
        `${apiRoutes.getCredentials}?${query}`,
        { id: user.id, accessToken: user.accessToken },
        companyId
      );
      const body = res.data || {};
      setCredentials(body.data ?? []);
      setSummary(body.summary ?? EMPTY_SUMMARY);
      setPagination(body.pagination ?? { page, limit, total: 0, totalPages: 0 });
      setFeatureGated(false);
    } catch (err) {
      // 403 is the `credentials` feature gate, not a failure — the page renders
      // a gated state rather than an error. Anything else IS an error and must
      // look like one: RecordTable renders a failed load distinctly from an
      // empty list, which is the whole reason it takes an `error` prop.
      if (err.response?.status === 403) setFeatureGated(true);
      else setError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, user?.id, user?.accessToken, query, page, limit]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  return { credentials, summary, pagination, loading, error, featureGated, refetch: fetchCredentials };
}
